import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_ATTENDANCE_EVENT_TYPES,
  HR_ATTENDANCE_EXCEPTION_STATUSES,
  HR_ATTENDANCE_SOURCES,
  HR_TIMESHEET_STATUSES,
  HrAttendanceEvent,
  HrAttendanceException,
  HrEmployee,
  HrTimeAdjustmentRequest,
  HrTimeDashboard,
  HrTimesheet,
  HrTimesheetGenerationRequest
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-time',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-time.component.html',
  styleUrls: ['./hr-time.component.scss']
})
export class HrTimeComponent implements OnInit {
  activeListTab = 'timesheets';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  dashboard: HrTimeDashboard = {};
  events: HrAttendanceEvent[] = [];
  timesheets: HrTimesheet[] = [];
  exceptions: HrAttendanceException[] = [];
  adjustments: HrTimeAdjustmentRequest[] = [];
  employees: HrEmployee[] = [];
  timesheetDialogVisible = false;
  selectedTimesheet?: HrTimesheet;
  exceptionStatus = 'OPEN';
  timesheetFilters: { status?: string; employeeId?: number } = {};

  eventForm: HrAttendanceEvent = this.emptyEvent();
  generationForm: HrTimesheetGenerationRequest = this.emptyGeneration();
  adjustmentForm: HrTimeAdjustmentRequest = this.emptyAdjustment();
  activeDialog?: 'event' | 'generation' | 'adjustment';

  readonly canManageTime = this.permissionService.hasPermission(app_permissions.hr.time.manage);
  readonly canApproveTime = this.permissionService.hasPermission(app_permissions.hr.time.approve);
  readonly canAdjustTime = this.permissionService.hasPermission(app_permissions.hr.time.adjust);
  readonly eventTypeOptions = HR_ATTENDANCE_EVENT_TYPES.map(value => ({ label: this.format(value), value }));
  readonly sourceOptions = HR_ATTENDANCE_SOURCES.map(value => ({ label: this.format(value), value }));
  readonly timesheetStatusOptions = HR_TIMESHEET_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly exceptionStatusOptions = HR_ATTENDANCE_EXCEPTION_STATUSES.map(value => ({ label: this.format(value), value }));

  ngOnInit(): void {
    this.loadEmployees();
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Events Today', value: this.dashboard.attendanceEventsToday ?? 0, tone: 'info' },
      { label: 'Open Exceptions', value: this.dashboard.openExceptions ?? 0, tone: (this.dashboard.openExceptions ?? 0) > 0 ? 'warn' : 'success' },
      { label: 'Draft Timesheets', value: this.dashboard.draftTimesheets ?? 0, tone: 'info' },
      { label: 'Submitted Timesheets', value: this.dashboard.submittedTimesheets ?? 0, tone: (this.dashboard.submittedTimesheets ?? 0) > 0 ? 'warn' : 'success' },
      { label: 'Approved Timesheets', value: this.dashboard.approvedTimesheets ?? 0, tone: 'success' },
      { label: 'Regular Hours', value: this.dashboard.regularHours ?? 0 },
      { label: 'Overtime Hours', value: this.dashboard.overtimeHours ?? 0, tone: (this.dashboard.overtimeHours ?? 0) > 0 ? 'warn' : 'success' },
      { label: 'Drive/Duty Hours', value: this.dashboard.driveDutyHours ?? 0, tone: 'info' }
    ];
  }

  get employeeOptions(): { label: string; value: number | undefined }[] {
    return this.employees.map(employee => ({
      label: this.employeeLabel(employee),
      value: employee.employeeId
    }));
  }

  load(): void {
    this.loading = true;
    this.hrService.timeDashboard().subscribe({
      next: response => {
        this.dashboard = response.data || {};
        this.events = this.dashboard.recentEvents || [];
        this.timesheets = this.dashboard.timesheets || [];
        this.exceptions = this.dashboard.openExceptionRows || [];
        this.loading = false;
        this.loadAdjustments();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to load time dashboard.' });
      }
    });
  }

  loadTimesheets(): void {
    this.hrService.timesheets(undefined, this.timesheetFilters.status, undefined, undefined, this.toNumber(this.timesheetFilters.employeeId), 250).subscribe({
      next: response => this.timesheets = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to load timesheets.' })
    });
  }

  loadExceptions(): void {
    this.hrService.attendanceExceptions(undefined, this.exceptionStatus, undefined, 250).subscribe({
      next: response => this.exceptions = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to load exceptions.' })
    });
  }

  loadAdjustments(): void {
    this.hrService.timeAdjustments(undefined, undefined, undefined, 250).subscribe({
      next: response => this.adjustments = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to load time adjustments.' })
    });
  }

  loadEmployees(): void {
    this.hrService.employees({ owningCompanyId: 250, pageSize: 500 }).subscribe({
      next: response => this.employees = response.items || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to load employee dropdown.' })
    });
  }

  recordEvent(): void {
    const request = this.cleanAttendanceEvent(this.eventForm);
    this.hrService.recordAttendanceEvent(request).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.messageService.add({ severity: 'success', summary: 'HR Time', detail: 'Attendance event recorded.' });
        this.resetEvent();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to record attendance event.' })
    });
  }

  generateTimesheets(): void {
    const request = { ...this.generationForm, employeeId: this.toNumber(this.generationForm.employeeId) };
    this.hrService.generateTimesheets(request).subscribe({
      next: response => {
        this.activeDialog = undefined;
        this.timesheets = response.data || [];
        this.messageService.add({ severity: 'success', summary: 'HR Time', detail: `${this.timesheets.length} timesheets generated.` });
        this.loadExceptions();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to generate timesheets.' })
    });
  }

  submitTimesheet(timesheet: HrTimesheet): void {
    if (!timesheet.timesheetId) return;
    this.hrService.submitTimesheet(timesheet.timesheetId, { reason: 'Submitted from HR time workspace.' }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Time', detail: 'Timesheet submitted.' });
        this.loadTimesheets();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to submit timesheet.' })
    });
  }

  approveTimesheet(timesheet: HrTimesheet): void {
    if (!timesheet.timesheetId) return;
    this.hrService.approveTimesheet(timesheet.timesheetId, { reason: 'Approved from HR time workspace.' }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Time', detail: 'Timesheet approved.' });
        this.loadTimesheets();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to approve timesheet.' })
    });
  }

  lockTimesheet(timesheet: HrTimesheet): void {
    if (!timesheet.timesheetId) return;
    this.hrService.lockTimesheet(timesheet.timesheetId, { reason: 'Locked from HR time workspace.' }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Time', detail: 'Timesheet locked.' });
        this.loadTimesheets();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to lock timesheet.' })
    });
  }

  resolveException(row: HrAttendanceException): void {
    if (!row.attendanceExceptionId) return;
    this.hrService.resolveAttendanceException(row.attendanceExceptionId, 'Resolved from HR time workspace.').subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Time', detail: 'Attendance exception resolved.' });
        this.loadExceptions();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to resolve exception.' })
    });
  }

  requestAdjustment(): void {
    const request = this.cleanAdjustment(this.adjustmentForm);
    this.hrService.requestTimeAdjustment(request).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.messageService.add({ severity: 'success', summary: 'HR Time', detail: 'Time adjustment requested.' });
        this.resetAdjustment();
        this.loadAdjustments();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to request time adjustment.' })
    });
  }

  approveAdjustment(adjustment: HrTimeAdjustmentRequest): void {
    if (!adjustment.timeAdjustmentRequestId) return;
    this.hrService.approveTimeAdjustment(adjustment.timeAdjustmentRequestId, 'Approved from HR time workspace.').subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Time', detail: 'Time adjustment approved.' });
        this.loadAdjustments();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to approve time adjustment.' })
    });
  }

  rejectAdjustment(adjustment: HrTimeAdjustmentRequest): void {
    if (!adjustment.timeAdjustmentRequestId) return;
    this.hrService.rejectTimeAdjustment(adjustment.timeAdjustmentRequestId, 'Rejected from HR time workspace.').subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Time', detail: 'Time adjustment rejected.' });
        this.loadAdjustments();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Time', detail: 'Unable to reject time adjustment.' })
    });
  }

  openTimesheet(timesheet: HrTimesheet): void {
    this.selectedTimesheet = timesheet;
    this.timesheetDialogVisible = true;
  }

  clearTimesheetFilters(): void {
    this.timesheetFilters = {};
    this.loadTimesheets();
  }

  resetEvent(): void {
    this.eventForm = this.emptyEvent();
  }

  resetGeneration(): void {
    this.generationForm = this.emptyGeneration();
  }

  resetAdjustment(): void {
    this.adjustmentForm = this.emptyAdjustment();
  }

  openActionDialog(dialog: 'event' | 'generation' | 'adjustment'): void {
    if (dialog === 'event') this.resetEvent();
    if (dialog === 'generation') this.resetGeneration();
    if (dialog === 'adjustment') this.resetAdjustment();
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: 'event' | 'generation' | 'adjustment'): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: 'event' | 'generation' | 'adjustment', visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  timesheetSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'APPROVED' || status === 'LOCKED') return 'success';
    if (status === 'SUBMITTED') return 'warn';
    if (status === 'DRAFT') return 'info';
    if (status === 'REOPENED') return 'danger';
    return 'secondary';
  }

  adjustmentSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'APPROVED') return 'success';
    if (status === 'PENDING') return 'warn';
    if (status === 'REJECTED') return 'danger';
    return 'secondary';
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  prettyPayroll(value?: string): string {
    if (!value) return '';
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  private employeeLabel(employee: HrEmployee): string {
    const name = employee.displayName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Employee';
    const number = employee.employeeNumber || employee.employeeId || '-';
    const department = employee.departmentName ? ` - ${employee.departmentName}` : '';
    return `${name} (${number})${department}`;
  }

  private emptyEvent(): HrAttendanceEvent {
    return {
      eventType: 'CLOCK_IN',
      source: 'WEB',
      eventTime: this.datetimeLocal(new Date())
    };
  }

  private emptyGeneration(): HrTimesheetGenerationRequest {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      periodStart: this.isoDate(firstDay),
      periodEnd: this.isoDate(lastDay)
    };
  }

  private emptyAdjustment(): HrTimeAdjustmentRequest {
    return {
      adjustmentType: 'MANUAL_ADJUSTMENT',
      eventDate: this.isoDate(new Date())
    };
  }

  private cleanAttendanceEvent(value: HrAttendanceEvent): HrAttendanceEvent {
    return {
      ...value,
      employeeId: this.toNumber(value.employeeId),
      deliveryId: this.toNumber(value.deliveryId),
      mobilizationId: this.toNumber(value.mobilizationId),
      warehouseId: this.toNumber(value.warehouseId)
    };
  }

  private cleanAdjustment(value: HrTimeAdjustmentRequest): HrTimeAdjustmentRequest {
    return {
      ...value,
      employeeId: this.toNumber(value.employeeId),
      hours: this.toNumber(value.hours),
      deliveryId: this.toNumber(value.deliveryId),
      mobilizationId: this.toNumber(value.mobilizationId)
    };
  }

  private toNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  private isoDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private datetimeLocal(date: Date): string {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }
}
